import React, { MouseEventHandler, ReactNode } from 'react'

import { Context } from 'types/GlobalUserEvents'
import { EPublicationStatus } from 'types/Enums'
import { OptionProps } from 'components_new/molecules/OptionsMenu'
import { SxProps, ThemeProvider, useMediaQuery, useTheme } from '@mui/material'

import { useTranslation } from 'language/translator'

import Box from 'components_new/atoms/Box'
import Card from 'components_new/atoms/Card'
import CardActionArea from 'components_new/atoms/Card/CardActionArea'
import CardContent from 'components_new/atoms/Card/CardContent'
import CardMedia from 'components_new/atoms/Card/CardMedia'
import Chip, { ChipProps } from 'components_new/atoms/Chip'
import Icon, { IconProps } from 'components_new/atoms/Icon'
import IconButton from 'components_new/atoms/IconButton'
import Tooltip from 'components_new/atoms/Tooltip'

import GenericAvatar, {
  GenericAvatarProps
} from 'components_new/molecules/GenericAvatar'
import GenericListItem, {
  GenericListItemProps
} from 'components_new/molecules/GenericListItem'
import OptionsMenuButton from 'components_new/molecules/OptionsMenuButton'

import { addRef } from 'utils/queryHandler'
import { getColorMode, getTheme } from 'themes'
import { useCustomization } from 'redux/hooks/Customization'

export interface GenericThumbnailProps {
  actions?: ReactNode
  AvatarProps?: GenericListItemProps['AvatarProps']
  body?: string
  chips?: ChipProps[]
  content?: ReactNode
  context?: Context // for metrics
  customTitle?: ReactNode
  disabled?: boolean
  favorite?: boolean
  href?: string
  icon?: IconProps
  id?: string
  onClick?: (() => void) | MouseEventHandler<HTMLElement>
  onClickFavorite?: () => void
  onClickOptionsMenu?: MouseEventHandler<HTMLElement>
  onClickUnlink?: MouseEventHandler<HTMLElement>
  options?: OptionProps[]
  media?: {
    avatar?: boolean // from 'variant'
    node?: ReactNode // custom media content (e.g. iframe preview)
    src?: string | null
  }
  title: string
  search?: string
  status?: EPublicationStatus
  sx?: SxProps
  variant?: GenericAvatarProps['variant']
}

const GenericThumbnail = React.forwardRef(
  (props: GenericThumbnailProps, ref: any) => {
    const {
      actions,
      AvatarProps,
      body,
      chips,
      content,
      context,
      customTitle,
      disabled,
      favorite,
      icon,
      media,
      onClick,
      onClickFavorite,
      onClickOptionsMenu,
      onClickUnlink,
      options,
      search,
      status,
      sx,
      title,
      variant
    } = props

    const { t } = useTranslation()
    const href =
      props.href && context ? addRef(props.href, context) : props.href

    const theme = useTheme()
    const lg = useMediaQuery(theme.breakpoints.up('lg'), {
      noSsr: true
    })

    return (
      <Card
        ref={ref}
        sx={{
          bgcolor: variant === 'folder' ? 'background.default' : undefined,
          position: 'relative',
          ...sx,
          ['&:hover']: {
            ['& .ThumbnailFavoriteButton']: {
              opacity: 1
            },
            ['& .ThumbnailActions']: {
              opacity: 1
            }
          }
        }}
        variant={variant === 'folder' ? undefined : 'outlined'}
        elevation={0}
      >
        <CardActionArea
          disabled={disabled}
          href={href}
          onClick={onClick}
          search={search}
        >
          {/*-- media --*/}
          {media ? (
            <MediaThemeModeWrapper {...props}>
              <Box sx={{ position: 'relative', overflow: 'hidden' }}>
                {/*-- image --*/}
                {media.src ? (
                  <CardMedia
                    component="img"
                    image={media.src}
                    sx={{
                      aspectRatio: '16 / 9',
                      objectFit: 'cover',
                      transition: 'all 0.2s ease',
                      ['&:hover']: { transform: 'scale(1.05)' }
                    }}
                  />
                ) : null}

                {/*-- avatar --*/}
                {media.avatar && variant && !media.src && !media.node ? (
                  <Box
                    sx={{
                      alignItems: 'center',
                      aspectRatio: '16 / 9',
                      bgcolor: 'action.hover',
                      display: 'flex',
                      justifyContent: 'center'
                    }}
                  >
                    <GenericAvatar
                      size="large"
                      title={title}
                      variant={variant}
                    />
                  </Box>
                ) : null}

                {/*-- custom node --*/}
                {media.node && !media.src ? (
                  <Box
                    sx={{
                      aspectRatio: '16 / 9',
                      overflow: 'hidden',
                      position: 'relative'
                    }}
                  >
                    {media.node}
                  </Box>
                ) : null}

                {/*-- adornments --*/}
                {chips && chips.length > 0 ? (
                  <Box
                    sx={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 0.5,
                      left: 8,
                      pointerEvents: 'none',
                      position: 'absolute',
                      top: 8
                    }}
                  >
                    {chips.map((chip, i) => (
                      <Chip key={i} size="small" {...chip} />
                    ))}
                  </Box>
                ) : null}

                {onClickFavorite && variant !== 'folder' ? (
                  <Tooltip
                    title={
                      status === EPublicationStatus.DRAFT
                        ? t('dashboards', 'favorite_unpublished_tooltip')
                        : favorite
                          ? t('dashboards', 'remove_favorite_tooltip')
                          : t('dashboards', 'add_favorite_tooltip')
                    }
                  >
                    <Box
                      className="ThumbnailFavoriteButton"
                      sx={{
                        right: 8,
                        position: 'absolute',
                        top: 8,
                        opacity: favorite ? 1 : 0
                      }}
                    >
                      <IconButton
                        disabled={status === EPublicationStatus.DRAFT}
                        onClick={(e) => {
                          e.preventDefault()
                          e.stopPropagation()
                          onClickFavorite()
                        }}
                        onMouseDown={(e) => e.stopPropagation()}
                        size="small"
                      >
                        <Icon
                          color={favorite ? 'warning' : undefined}
                          fontSize="small"
                          name="Favorite"
                        />
                      </IconButton>
                    </Box>
                  </Tooltip>
                ) : null}

                {status ? (
                  <Chip
                    color={
                      status === EPublicationStatus.PUBLISHED
                        ? 'primary'
                        : undefined
                    }
                    label={
                      status === EPublicationStatus.PUBLISHED
                        ? t('global', 'published')
                        : t('global', 'draft')
                    }
                    size="small"
                    sx={{
                      bottom: 8,
                      left: 8,
                      pointerEvents: 'none',
                      position: 'absolute'
                    }}
                  />
                ) : null}
              </Box>
            </MediaThemeModeWrapper>
          ) : null}

          {/*-- content --*/}
          <CardContent
            sx={{
              alignItems: 'center',
              display: 'flex',
              gap: 1.5,
              px: lg ? 1.5 : 1.25,
              py: lg ? 1.5 : !media ? 1 : 0.5
            }}
          >
            <Tooltip enterDelay={500} title={title}>
              <GenericListItem
                AvatarProps={AvatarProps}
                body={body}
                component="div"
                customTitle={customTitle}
                disablePadding={true}
                icon={icon}
                size={lg ? 'medium' : 'small'}
                title={title}
                sx={{ minWidth: 0 }}
                variant={variant}
              />
            </Tooltip>

            {content ? (
              <Box
                sx={{
                  alignItems: 'center',
                  display: 'flex',
                  gap: 0.25
                }}
              >
                {content}
              </Box>
            ) : null}

            {actions || options || onClickOptionsMenu || onClickUnlink ? (
              <Box
                className="ThumbnailActions"
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                }}
                onMouseDown={(e) => e.stopPropagation()}
                sx={{
                  alignItems: 'center',
                  display: 'flex',
                  gap: 0.25,
                  opacity: 0
                }}
              >
                {actions}

                {onClickUnlink ? (
                  <Tooltip title={t('global', 'unlink')}>
                    <IconButton onClick={onClickUnlink} size="small">
                      <Icon fontSize="small" name="LinkOff" />
                    </IconButton>
                  </Tooltip>
                ) : null}

                {options || onClickOptionsMenu ? (
                  <OptionsMenuButton
                    edge="end"
                    onClick={onClickOptionsMenu}
                    options={options}
                    size="small"
                  />
                ) : null}
              </Box>
            ) : null}
          </CardContent>
        </CardActionArea>
      </Card>
    )
  }
)

GenericThumbnail.displayName = 'GenericThumbnail'
export default GenericThumbnail

// Determines the theme mode based on the customer's paper background color.
// Paper is used as the reference since it covers the largest area of the
// dashboard.
const MediaThemeModeWrapper = (
  props: GenericThumbnailProps & { children: ReactNode }
) => {
  const { children, media, variant } = props

  const customization = useCustomization()
  const customPaperColor = customization.background_colors?.paper

  const mode = customPaperColor ? getColorMode(customPaperColor) : 'light'

  if (media?.src && variant === 'dashboard')
    return <ThemeProvider theme={getTheme(mode)}>{children}</ThemeProvider>

  return <>{children}</>
}
